import { useCallback, useState } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { APP_EVENTS, captureAppEvent } from 'src/services/posthogEvents';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CancelOrderConfirmationModal from 'src/components/CancelOrderConfirmationModal';
import CancelOrderSuccessModal from 'src/components/CancelOrderSuccessModal';

import { Api } from 'src/models/Api.model';
import { DeviceDataView } from 'src/models/GalenData/DeviceDataView.model';
import { OrderData, OrderStatusEnum } from 'src/models/GalenData/Order.model';
import { I18NextNS } from 'src/models/Language.model';
import { Product } from 'src/models/Product.model';

import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';

export type OrderDetailsDrawerProps = {
  show: boolean;
  onHide: () => void;
  order?: DeviceDataView<OrderData>;
  showOrderStatus?: boolean;
  onOrderCancelled?: () => void;
};

function Field({
  label,
  value,
  dataTest,
}: {
  label: string;
  value?: string;
  dataTest: string;
}) {
  return (
    <div>
      <div className="text-gray-400">{label}</div>
      <div className="d-flex align-items-center gap-2">
        <span data-test={dataTest}>{value}</span>
      </div>
    </div>
  );
}

export default function OrderDetailsDrawer({
  show,
  onHide,
  order,
  showOrderStatus = false,
  onOrderCancelled,
}: OrderDetailsDrawerProps) {
  const { t } = useTranslation(I18NextNS.COMMON);
  const { showHttpRequestError } = useHandleHttpRequestError();
  const {
    practiceSettings: { product },
  } = useRemoteBoundStore();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showCancelSuccess, setShowCancelSuccess] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const handleHide = useCallback(() => {
    setShowCancelConfirm(false);
    onHide();
  }, [onHide]);

  const orderId = order?.data?.OrderId?.value;
  const orderStatus = order?.data?.Status?.value;

  const canCancelOrder =
    Boolean(onOrderCancelled) &&
    product === Product.TELEOPHTH_STANDALONE &&
    orderStatus === OrderStatusEnum.OPEN;

  const handleConfirmCancelOrder = useCallback(async () => {
    if (!order) return;
    try {
      setIsCancelling(true);
      await Api.remote.noRetry.update_exam_order(order, {
        status: OrderStatusEnum.CLOSED,
      });
      captureAppEvent(APP_EVENTS.ORDER_CANCELLED, { order_id: orderId });
      setShowCancelConfirm(false);
      setShowCancelSuccess(true);
    } catch (error) {
      showHttpRequestError(error);
    } finally {
      setIsCancelling(false);
    }
  }, [order, orderId, showHttpRequestError]);

  const handleCloseCancelSuccess = useCallback(() => {
    setShowCancelSuccess(false);
    onOrderCancelled?.();
    onHide();
  }, [onOrderCancelled, onHide]);

  return (
    <>
      <Offcanvas
        show={show}
        onHide={handleHide}
        placement="end"
        keyboard={false}
        className="w-25"
        data-test="order-details-drawer"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title as="h4" className="fw-bold mt-3">
            {t('orderDetails.title')}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <hr />
        <Offcanvas.Body className="d-flex flex-column">
          <h6 className="fw-bold mb-3">{t('orderDetails.patient')}</h6>

          <div className="d-flex flex-column gap-4">
            <div className="row g-4">
              <div className="col-6">
                <Field
                  label={t('orderDetails.firstName')}
                  value={order?.owner?.firstName}
                  dataTest="order-details-first-name"
                />
              </div>
              <div className="col-6">
                <Field
                  label={t('orderDetails.lastName')}
                  value={order?.owner?.lastName}
                  dataTest="order-details-last-name"
                />
              </div>
              <div className="col-6">
                <Field
                  label={t('orderDetails.dateOfBirth')}
                  value={order?.owner?.dateOfBirth}
                  dataTest="order-details-dob"
                />
              </div>
              <div className="col-6">
                <Field
                  label={t('orderDetails.patientId')}
                  value={order?.owner?.patientId}
                  dataTest="order-details-patient-id"
                />
              </div>
            </div>

            <Field
              label={t('orderDetails.site')}
              value={order?.data?.SiteName?.value}
              dataTest="order-details-site"
            />

            <Field
              label={t('orderDetails.orderId')}
              value={orderId}
              dataTest="order-details-order-id"
            />

            {showOrderStatus && orderStatus && (
              <>
                <hr className="my-2" />
                <h6 className="fw-bold">{t('orderDetails.exam')}</h6>
                <Field
                  label={t('orderDetails.status')}
                  value={t(`orderFields.orderStatusEnum.${orderStatus}`)}
                  dataTest="order-details-status"
                />
              </>
            )}
          </div>

          {canCancelOrder && (
            <Button
              variant="outline-danger"
              className="mt-auto w-100 py-2"
              onClick={() => setShowCancelConfirm(true)}
              data-track="exam.order-details.cancel-order"
              data-test="order-details-cancel-exam"
              data-observe
            >
              {t('orderDetails.cancelOrderTitle')}
            </Button>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      <CancelOrderConfirmationModal
        show={show && showCancelConfirm}
        order={order}
        isCancelling={isCancelling}
        onGoBack={() => setShowCancelConfirm(false)}
        onConfirm={handleConfirmCancelOrder}
      />

      <CancelOrderSuccessModal
        show={showCancelSuccess}
        onClose={handleCloseCancelSuccess}
      />
    </>
  );
}
